import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmResetPasswordModel } from '@ibc-features/user/models/auth.model';
import { AuthService } from '@ibc-features/user/services/auth.service';
import { WindowComponent } from '@ibc-shared/components/window/window.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-recover-password-form',
  imports: [WindowComponent, TranslateModule, ReactiveFormsModule, NgTemplateOutlet],
  templateUrl: './recover-password-form.component.html',
  styleUrl: './recover-password-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecoverPasswordFormComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected token = signal<string>('');

  protected form = new FormGroup(
    {
      password: new FormControl('', [Validators.required, Validators.minLength(8)]),
      repeatPassword: new FormControl('', [Validators.required]),
    },
    { validators: this.passwordMatchValidator }
  );

  protected recoveryComplete = signal<boolean>(false);
  protected isSubmitting = signal(false);
  protected errorMessage = signal(false);

  public ngOnInit(): void {
    const fromSnapshot = this.route.snapshot.queryParamMap.get('token') ?? this.route.parent?.snapshot.queryParamMap.get('token') ?? '';
    const fromRouter = this.router.parseUrl(this.router.url).queryParams['token'] ?? '';

    this.token.set(fromSnapshot || fromRouter);
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const repeatPassword = control.get('repeatPassword')?.value;

    if (!password || !repeatPassword) return null;

    return password === repeatPassword ? null : { passwordMismatch: true };
  }

  get passwordMismatch(): boolean {
    return (this.form.hasError('passwordMismatch') && this.form.get('repeatPassword')?.touched) || false;
  }

  protected getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;

    if (errors['required']) {
      return 'features.register.errors.requiredField';
    }

    switch (controlName) {
      case 'password':
        return 'features.register.errors.passwordMinLength';
    }

    return '';
  }

  protected hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.errors && control.touched);
  }

  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
    }

    const token = this.token();
    if (!token) {
      this.errorMessage.set(true);
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(false);

    const value = this.form.value;
    const payload: ConfirmResetPasswordModel = {
      token: token,
      newPassword: value.password ?? '',
    };

    this.auth.resetPasswordConfirmation(payload).subscribe({
      next: () => {
        this.form.reset();
        this.recoveryComplete.set(true);
        this.isSubmitting.set(false);

        this.router.navigate([], { queryParams: {}, replaceUrl: true });
      },
      error: () => {
        this.form.reset();
        this.isSubmitting.set(false);
        this.errorMessage.set(true);
      },
    });
  }
}
